import React, { useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Badge, Table, Loader, ScrollArea,
  SimpleGrid, SegmentedControl,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—');

// Window presets → a `from` date (YYYY-MM-DD), computed client-side (Arizona-local dates).
const WINDOWS = { 'All time': null, '7 days': 7, '30 days': 30, '90 days': 90 };
function fromParam(days) {
  if (!days) return '';
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function ManagerOverview() {
  const navigate = useNavigate();
  const [win, setWin] = useState('30 days');

  const url = useMemo(() => {
    const f = fromParam(WINDOWS[win]);
    return `${config.apiBaseUrl}/api/rotabull/opportunities/summary${f ? `?from=${f}` : ''}`;
  }, [win]);
  const { data, isLoading, error } = useAuthenticatedData(url);

  const reps = data?.bySalesperson || [];
  const unassigned = reps.find((r) => r.ccode === '__unassigned__');
  const house = reps.find((r) => r.ccode === 'ITS');
  const total = data?.total || 0;
  const routable = total - (unassigned?.deals || 0);

  const openStream = (ccode) => navigate(`/sales/funnel/my-deals?sp=${encodeURIComponent(ccode)}`);

  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <Group justify="space-between" align="center">
        <div>
          <Title order={2}>Manager Overview</Title>
          <Text size="sm" c="dimmed">Inbound Rotabull deals and how they route to the sales team.</Text>
        </div>
        <SegmentedControl size="xs" value={win} onChange={setWin} data={Object.keys(WINDOWS)} />
      </Group>

      {isLoading && <Group justify="center" p="xl"><Loader /></Group>}
      {error && <Text c="red">Failed to load overview.</Text>}

      {!isLoading && !error && data && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
            <Kpi label="Total inbound" value={num(total)} />
            <Kpi label="Routable" value={num(routable)} sub={pct(routable, total)} accent="teal" />
            <Kpi label="Unassigned" value={num(unassigned?.deals || 0)} sub={pct(unassigned?.deals || 0, total)} accent="orange" />
            <Kpi label="House (ITS)" value={num(house?.deals || 0)} sub={pct(house?.deals || 0, total)} accent="blue" />
            <Kpi label="Quoted" value={num(data.quotedCount)} sub={pct(data.quotedCount, total)} />
            <Kpi label="Key customers" value={num(data.keyCustomerCount)} />
          </SimpleGrid>

          <Paper withBorder radius="md">
            <Group justify="space-between" p="sm" pb={4}>
              <Text fw={600} size="sm">By salesperson</Text>
              <Text size="xs" c="dimmed">{reps.length} owners · click a row to open their stream</Text>
            </Group>
            <ScrollArea>
              <Table fz="sm" striped highlightOnHover miw={720}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Salesperson</Table.Th>
                    <Table.Th ta="right">Deals</Table.Th>
                    <Table.Th ta="right">Actionable</Table.Th>
                    <Table.Th ta="right">Quoted</Table.Th>
                    <Table.Th ta="right">Key</Table.Th>
                    <Table.Th ta="right">Actionable %</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {reps.map((r) => {
                    const special = r.ccode === '__unassigned__' || r.ccode === 'ITS';
                    return (
                      <Table.Tr key={r.ccode} style={{ cursor: 'pointer' }} onClick={() => openStream(r.ccode)}>
                        <Table.Td>
                          <Group gap={6}>
                            <Text fw={special ? 700 : 500} size="sm"
                              c={r.ccode === '__unassigned__' ? 'orange' : r.ccode === 'ITS' ? 'blue' : undefined}>
                              {r.name}
                            </Text>
                            {r.ccode !== '__unassigned__' && (
                              <Badge size="xs" variant="light" color={r.active ? 'gray' : 'red'}>
                                {r.ccode}{r.active ? '' : ' · inactive'}
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td ta="right" fw={600}>{num(r.deals)}</Table.Td>
                        <Table.Td ta="right">{num(r.actionable)}</Table.Td>
                        <Table.Td ta="right">{num(r.quoted)}</Table.Td>
                        <Table.Td ta="right">{num(r.keyCount)}</Table.Td>
                        <Table.Td ta="right">{pct(r.actionable, r.deals)}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {reps.length === 0 && <Table.Tr><Table.Td colSpan={6}><Text c="dimmed" ta="center" py="md">No deals in this window.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        </>
      )}
    </Stack>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Text size="xs" c="dimmed">{label}</Text>
      <Title order={2} c={accent}>{value}</Title>
      {sub && <Text size="xs" c="dimmed">{sub}</Text>}
    </Paper>
  );
}
